import { Box, Grid, darken, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';

import { Page } from '../../components';
import { ConfirmationDialogue } from '../../components/modal/ConfirmationDialogue';
import { UnsavedChanges } from '../../components/modal/UnsavedChanges';
import { AdminTable } from '../../components/table/AdminTable';
import { IAgentCommissionAPI } from '../../fetch';
import { stripMoneyFormat } from '../../helpers';
import { useToastContext } from '../../hooks';
import { IAgentCommission, IAgentCommissionTier } from '../../models';
import { theme } from '../../styles';
import { deepEqual } from 'fast-equals';
import { CommissionNames } from './CommissionNames';
/**
 * Presentational component allowing a user to click a Commission Table on the
 * left, auto-hiding and showing data relevant to that table ID in the
 * right-side column.
 */
export const CommissionTables: FC = () => {
  // Non-state
  const { showToast } = useToastContext();
  const classes = useStyles();

  // Network state
  const [selectedCommission, setSelectedCommission] = useState<IAgentCommission | null>(null);
  const [commissions, setCommissions] = useState<IAgentCommission[]>([]);
  const [commissionsCopy, setCommissionsCopy] = useState<IAgentCommission[]>([]);

  // Non-network state
  const [newElementIndex, setNewElementIndex] = useState(-1);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<false | IAgentCommissionTier>(false);

  const fetchCommissions = useCallback(async () => {
    setIsLoading(true);
    try {
      const list = await IAgentCommissionAPI.getAll();
      setCommissions(list);
      setCommissionsCopy(JSON.parse(JSON.stringify(list)));
      // Select a default table
      if (list.length > 0) setSelectedCommission(list[0]);
    } catch (err) {
      const message = err instanceof Error && err.message;
      showToast('error', message || 'Failed to fetch commissions.');
    } finally {
      setIsLoading(false);
    }
    // Ignore showToast dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    fetchCommissions();
    // Fetch only on component mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeCommissionTierRow = useCallback((tier: IAgentCommissionTier) => {
    setCommissions((state: IAgentCommission[]) => {
      const commissionIndex = state.findIndex(_ => _.agentCommissionId === tier.commissionTable);

      // Invalid index, skip.
      if (commissionIndex === -1) return state;

      const cloned = state.slice();
      // Remove row in place
      const tiers = cloned[commissionIndex].agentCommissionTiers;
      tiers.splice(tiers.indexOf(tier), 1);

      return cloned;
    });
  }, []);

  const commissionsColumns = useMemo(() => {
    return [
      {
        accessor: 'lowRange',
        Header: 'Low',
        disableSortBy: true,
        sort: false,
        Cell: ({
          cell: {
            row: { original },
          },
        }: {
          cell: { row: { original: IAgentCommissionTier } };
        }) => {
          const tier = original;

          return (
            <NumberFormat
              // Low range is always disabled.
              disabled
              fullWidth
              isNumericString
              thousandSeparator
              decimalScale={0}
              tabIndex={-1}
              size="small"
              prefix="$"
              name="lowRange"
              value={tier.lowRange ?? 0}
              customInput={TextField}
              inputProps={{ style: { textAlign: 'right' } }}
            />
          );
        },
      },
      {
        accessor: '',
        Header: 'High',
        disableSortBy: true,
        sort: false,
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IAgentCommissionTier; index: number } };
        }) => {
          const tier = original;

          const isLastRow =
            commissions.find(_ => _.agentCommissionId === tier.commissionTable)
              ?.agentCommissionTiers?.length ===
            index + 1;

          return (
            <NumberFormat
              fullWidth
              isNumericString
              thousandSeparator
              autoFocus={index === newElementIndex}
              size="small"
              prefix="$"
              name="highRange"
              decimalScale={0}
              value={tier?.highRange ?? null}
              customInput={TextField}
              inputProps={{
                style: { textAlign: 'right' },
                className: 'commission-tier-highRange-input',
              }}
              error={
                typeof tier?.highRange === 'number' && tier.lowRange! > tier.highRange && !isLastRow
              }
              // Show warning if invalid
              placeholder={tier?.highRange === null ? 'Enter High Range...' : undefined}
              label={
                typeof tier?.highRange === 'number' &&
                tier.lowRange! > tier.highRange! &&
                !isLastRow
                  ? `Must be > ${Intl.NumberFormat('en-US', {
                      maximumFractionDigits: 0,
                      style: 'currency',
                      currency: 'USD',
                    }).format(tier.lowRange!)}.`
                  : undefined
              }
              // Make tabs work
              onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                if (event.key === 'Tab') {
                  const node = document.getElementById(`commission-percentage-input-${index}`);
                  node?.focus?.();
                }
              }}
              onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                const value = event.currentTarget.value;
                // Allow high range to be blank (null) if the input is cleared
                const newHighRange = value === '' ? null : stripMoneyFormat(value);

                // Invalid string, skip.
                if (newHighRange !== null && !newHighRange) return;

                setHasChanges(true);
                setNewElementIndex(-1);
                // Update high range for edited row
                setCommissions((state: IAgentCommission[]) => {
                  const commissionIndex = state.findIndex(
                    _ => _.agentCommissionId === tier.commissionTable
                  );

                  // Invalid index, skip.
                  if (commissionIndex === -1) return state;

                  const cloned: IAgentCommission[] = state.slice();

                  // Update the low range for the next element
                  if (!!cloned[commissionIndex].agentCommissionTiers?.[index + 1]) {
                    cloned[commissionIndex].agentCommissionTiers[index + 1].isEdited = true;
                    if (newHighRange) {
                      cloned[commissionIndex].agentCommissionTiers[index + 1].lowRange =
                        newHighRange + 1;
                    }
                  }

                  cloned[commissionIndex].agentCommissionTiers[index] = {
                    ...tier,
                    isEdited: true,
                    highRange: newHighRange,
                  };

                  return cloned;
                });
              }}
              onValueChange={values => values.value}
              onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                event.target?.select?.();
              }}
            />
          );
        },
      },
      {
        accessor: 'commissionPercentage',
        Header: 'Percentage',
        disableSortBy: true,
        sort: false,
        overrideWidth: '12rem',
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IAgentCommissionTier; index: number } };
        }) => {
          const tier = original;

          return (
            <NumberFormat
              fullWidth
              isNumericString
              size="small"
              name="percentage"
              suffix="%"
              decimalScale={0}
              value={(tier.commissionPercentage * 100).toFixed(0)}
              customInput={TextField}
              inputProps={{
                style: { textAlign: 'right' },
                id: `commission-percentage-input-${index}`,
              }}
              onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                let value = event.currentTarget.value;
                // Cut the percentage sign off
                value = value.slice(0, value.length - 1);

                // Update the percentage
                setCommissions((state: IAgentCommission[]) => {
                  const commissionIndex = state.findIndex(
                    _ => _.agentCommissionId === tier.commissionTable
                  );
                  // Invalid index, skip.
                  if (commissionIndex === -1) return state;

                  const newCommissionPercentage: number = Number.parseInt(value, 10) / 100;

                  if (tier.commissionPercentage !== newCommissionPercentage) {
                    setHasChanges(true);
                  }

                  state[commissionIndex].agentCommissionTiers[index] = {
                    ...tier,
                    isEdited: true,
                    commissionPercentage: newCommissionPercentage,
                  };
                  return state;
                });
              }}
              onValueChange={values => values.value}
              onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                event.target?.select?.();
              }}
            />
          );
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commissions, hasChanges, newElementIndex, selectedCommission]);

  return (
    <Page title="Commission Tables">
      <Box
        sx={theme => ({
          display: 'flex',
          width: '100%',
          height: '100%',
          margin: '0 auto',
        })}
      >
        <UnsavedChanges dirty={hasChanges} />

        {/** CONTAINER OF BOTH TABLES */}
        <Grid container direction="row" gap="5rem">
          {/** LEFT CONTAINER */}
          <Grid
            item
            sx={theme => ({
              maxWidth: '18rem',
              borderRightStyle: 'solid',
              borderWidth: '2px',
              borderColor: theme.frankie.secondary,
              flexGrow: 1,
              '.MuiTableCell-root': {
                paddingRight: '0',
              },
              paddingBlock: theme.spacing(1),
            })}
          >
            <CommissionNames
              commissions={commissions}
              setCommissions={setCommissions}
              commissionsCopy={commissionsCopy}
              setSelectedCommission={setSelectedCommission}
              selectedCommission={selectedCommission}
              setNewElementIndex={setNewElementIndex}
              hasChanges={hasChanges}
              isLoading={isLoading}
            />
          </Grid>
          <Grid
            sx={theme => ({
              flexGrow: 1,
              '.MuiTableRow-root .MuiTableCell-root:first-of-type': {
                paddingLeft: '0',
              },
            })}
          >
            <AdminTable
              data={
                commissions.find(_ => _.agentCommissionId === selectedCommission?.agentCommissionId)
                  ?.agentCommissionTiers ?? []
              }
              columns={commissionsColumns}
              isLoading={isLoading}
              headerClasses={classes.header}
              hasChanges={!deepEqual(commissions, commissionsCopy)}
              isSaving={isSaving}
              onAdd={() => {
                if (!selectedCommission) return;
                // Add new empty row
                setCommissions((state: IAgentCommission[]) => {
                  const commissionIndex = state.findIndex(
                    _ => _.agentCommissionId === selectedCommission.agentCommissionId
                  );
                  if (commissionIndex === -1) {
                    return state;
                  }
                  const cloned = state.slice();
                  setNewElementIndex(cloned[commissionIndex].agentCommissionTiers.length);
                  // add row
                  const tiers = cloned[commissionIndex].agentCommissionTiers;
                  let lowRange = 0;
                  if (tiers?.length > 0) {
                    const last = tiers?.[tiers?.length - 1];
                    if (last?.highRange) {
                      lowRange = last.highRange + 1;
                    }
                  }
                  const { agentCommissionId } = selectedCommission;
                  cloned[commissionIndex].agentCommissionTiers.push(
                    IAgentCommission.getDefaultTier({ agentCommissionId, lowRange })
                  );
                  return cloned;
                });
              }}
              onSave={async function upsertAgentCommissions() {
                setIsSaving(true);
                try {
                  const cleanedCommissions = commissions.map(commission => {
                    if (commission.agentCommissionId < 0) {
                      commission.agentCommissionId = 0;
                      commission.agentCommissionTiers.map(tier => {
                        if (tier.commissionTable < 0) {
                          tier.commissionTable = 0;
                        }
                        return tier;
                      });
                    }
                    return commission;
                  });

                  const newCommissions = await IAgentCommissionAPI.upsert(cleanedCommissions);
                  // After successful update, update UI
                  setCommissions(newCommissions);
                  setCommissionsCopy(JSON.parse(JSON.stringify(newCommissions)));
                  setSelectedCommission(
                    newCommissions.find(
                      commission => commission.name === selectedCommission?.name
                    ) || commissions[0]
                  );
                  // Reset save button to disabled
                  setHasChanges(false);
                  showToast('success', 'Successfully saved commissions.');
                } catch (error) {
                  const message =
                    error instanceof Error ? error.message : 'Failed to save commissions.';
                  showToast('error', message);
                } finally {
                  setIsSaving(false);
                }
              }}
              onDelete={(tier, _index) => {
                // If deleting a new tier (ID = 0), do not pop modal, just delete and pop toast.
                if (tier.commissionTierId === 0) {
                  removeCommissionTierRow(tier);
                  showToast('info', 'Removed unsaved commission tier.');
                } else {
                  // Pop the delete confirm modal and set the data-to-delete
                  setEntryToDelete(tier);
                }
              }}
            />
          </Grid>
        </Grid>
      </Box>
      {!!entryToDelete && (
        <ConfirmationDialogue
          open
          id="delete-agent-commission-tier"
          title="Delete Commission Tier"
          onClose={() => setEntryToDelete(false)}
          onConfirm={async function deleteCommissionTier() {
            // Ensure data exists
            if (!entryToDelete) {
              return;
            }
            // Hit the API and delete
            try {
              await IAgentCommissionAPI.delete(entryToDelete.commissionTierId);
              // Update UI after successful delete
              removeCommissionTierRow(entryToDelete);
              showToast('success', 'Successfully deleted commission tier.');
            } catch (err) {
              const message =
                err instanceof Error ? err.message : 'Failed to delete commission tier.';
              showToast('error', message);
            } finally {
              setEntryToDelete(false);
            }
          }}
        />
      )}
    </Page>
  );
};

const useStyles = makeStyles(() => ({
  header: {
    textAlign: 'center',
  },
  addCommissionTableButton: {
    transition: 'padding 160ms ease-in',
    width: '100%',
    color: theme.frankie.searchBlue,
    backgroundColor: theme.palette.grey[300],
    padding: theme.spacing(0.465),
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'left',
    marginTop: 6,
    justifyContent: 'space-around',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,

    '&:hover': {
      backgroundColor: darken(theme.palette.grey[300], 0.1),
      paddingInline: theme.spacing(1, 0.535),
    },
  },
  CommissionTableName: {
    margin: theme.spacing(1, 0),
  },
}));
